import type { ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link, { type LinkProps } from 'next/link';
import { ChevronRightIcon } from '@/icons';
import { buttonVariants } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
interface SectionHeaderProps {
  title: string;
  /** 제목 오른쪽 개수. `undefined` 면 그리지 않는다. */
  count?: ReactNode;
  /** 전체 목록으로 가는 링크. 주면 개수 오른쪽에 chevron 이 붙고 누를 수 있게 된다. */
  href?: LinkProps['href'];
}

/**
 * 폴더·퀴즈 섹션의 제목 줄 (제목 + 개수). 내 퀴즈와 남의 프로필이 같은 모양으로 쓴다.
 *
 * 개수의 모양은 `buttonVariants` 에서 가져온다. `Button` 컴포넌트는 쓰지 않는다 —
 * 링크일 때는 `<a>` 가 필요하고, 링크가 아닐 때는 누를 것이 없다.
 */
export const SectionHeader = ({
  title,
  count,
  href
}: SectionHeaderProps) => {
  return <div className={'flex items-center gap-xs pt-sm'} data-sentry-component="SectionHeader" data-sentry-source-file="SectionHeader.tsx">
      {/*
        줄 높이는 개수 유무와 무관하게 48px 이다. 개수가 없다고 줄이 짧아지면 두 섹션 제목의 높이가 달라진다.
        `min-h` 는 패딩을 포함하므로(border-box) 40 이 아니라 48 을 준다.
       */}
      <div className={'flex min-h-[48px] min-w-px flex-1 items-center gap-none p-3xs'}>
        <p className={'truncate text-heading-lg-extrabold text-default'}>
          {title}
        </p>
        {count !== undefined && <CountBadge count={count} href={href} />}
      </div>
    </div>;
};

/** 제목 옆 개수. 링크가 있으면 chevron 이 붙은 앵커, 없으면 모양만 같은 글자다. */
const CountBadge = ({
  count,
  href
}: {
  count: ReactNode;
  href?: LinkProps['href'];
}) => {
  const shape = buttonVariants({
    variant: 'transparent',
    intent: 'neutral',
    size: 'md'
  });
  if (href != null && href !== '') {
    return <Link href={href} prefetch={false} className={cn(shape, 'no-underline')}>
        {count}
        <ChevronRightIcon size={'sm'} fill={ICON_COLOR.neutral} />
      </Link>;
  }

  /* 갈 곳이 없으면 hover 도 없다 — 모양만 빌리고 상호작용 암시는 지운다. */
  return <span className={cn(shape, 'hover:bg-transparent')} data-sentry-component="CountBadge" data-sentry-source-file="SectionHeader.tsx">{count}</span>;
};